const CRISP_NUDGE_PREFIX = "crisp_nudge";
const CRISP_NUDGE_TIMEZONE = "Asia/Kolkata";
const CRISP_NUDGE_START_HOUR_IST = 10; // 10:00 AM IST
const CRISP_NUDGE_END_HOUR_IST = 1; // 1:00 AM IST (next day)

export default {
  methods: {
    getCurrentISTDateForNudges() {
      const now = new Date();
      return new Date(
        now.toLocaleString("en-US", { timeZone: CRISP_NUDGE_TIMEZONE })
      );
    },

    isWithinCrispNudgeHours() {
      const istNow = this.getCurrentISTDateForNudges();
      const hour = istNow.getHours();
      // Valid window: 10:00 AM -> 12:59 AM IST
      return (
        hour >= CRISP_NUDGE_START_HOUR_IST || hour < CRISP_NUDGE_END_HOUR_IST
      );
    },

    getCrispClient() {
      if (typeof window === "undefined") return null;
      // Support both global styles used in the app.
      return window.$crisp || (typeof $crisp !== "undefined" ? $crisp : null); // eslint-disable-line no-undef
    },

    buildCrispNudgeStorageKey(key, { scopeToRoute = false } = {}) {
      if (!key) return "";

      const routeName =
        scopeToRoute && this.$route && this.$route.name
          ? String(this.$route.name)
          : "global";

      return `${CRISP_NUDGE_PREFIX}:${routeName}:${key}`;
    },

    getNudgeStorage(storage = "session") {
      if (typeof window === "undefined") return null;
      return storage === "local" ? window.localStorage : window.sessionStorage;
    },

    getLastCrispNudgeTimestamp(
      key,
      { storage = "session", scopeToRoute = false } = {}
    ) {
      const storageKey = this.buildCrispNudgeStorageKey(key, { scopeToRoute });
      if (!storageKey) return null;

      const targetStorage = this.getNudgeStorage(storage);
      if (!targetStorage) return null;

      try {
        const rawValue = targetStorage.getItem(storageKey);
        if (!rawValue) return null;
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : null;
      } catch (error) {
        return null;
      }
    },

    canShowCrispNudge(
      key,
      { cooldownMs = 0, storage = "session", scopeToRoute = false } = {}
    ) {
      if (!key) return false;

      const lastShownAt = this.getLastCrispNudgeTimestamp(key, {
        storage,
        scopeToRoute
      });
      if (!lastShownAt) return true;

      const elapsedMs = Date.now() - lastShownAt;
      return elapsedMs >= cooldownMs;
    },

    markCrispNudgeShown(
      key,
      { storage = "session", scopeToRoute = false } = {}
    ) {
      const storageKey = this.buildCrispNudgeStorageKey(key, { scopeToRoute });
      if (!storageKey) return;

      const targetStorage = this.getNudgeStorage(storage);
      if (!targetStorage) return;

      try {
        targetStorage.setItem(storageKey, String(Date.now()));
      } catch (error) {
        // Ignore storage issues to avoid blocking the UI flow.
      }
    },

    clearCrispNudge(key, { storage = "session", scopeToRoute = false } = {}) {
      const storageKey = this.buildCrispNudgeStorageKey(key, { scopeToRoute });
      if (!storageKey) return;

      const targetStorage = this.getNudgeStorage(storage);
      if (!targetStorage) return;

      try {
        targetStorage.removeItem(storageKey);
      } catch (error) {
        // Ignore storage cleanup errors.
      }
    },

    showCrispNudge({
      key,
      text,
      cooldownMs = 0,
      storage = "session",
      scopeToRoute = false,
      sendAsMessage = false
    } = {}) {
      const crisp = this.getCrispClient();
      if (!crisp || !key || !text) return false;
      if (!this.isWithinCrispNudgeHours()) return false;

      if (
        !this.canShowCrispNudge(key, {
          cooldownMs,
          storage,
          scopeToRoute
        })
      ) {
        return false;
      }
      if (sendAsMessage) {
        crisp.push(["do", "message:send", ["text", text]]);
      } else {
        crisp.push(["do", "message:show", ["text", text]]);
      }

      this.markCrispNudgeShown(key, { storage, scopeToRoute });

      return true;
    }
  }
};
